import { Paperclip } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { Button } from '../../../../components/Button';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput, TdSeamless, TdSelect } from '../../../../components/inline-add-table/cells';
import { DateField } from '../../../../components/inputs/DateField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { Stepper } from '../../../../components/Stepper';
import { useToast } from '../../../../components/toastContext';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { useRecruitmentUsers } from '../../vacancies/useRecruitmentUsers';
import { applicantRequirementsApi, applicantsApi } from '../applicantsApi';
import {
  APPLICANT_QUALIFICATION,
  APPLICANT_QUALIFICATION_OPTIONS,
  type ApplicantListItem,
  type ApplicantQualificationValue,
} from '../applicantTypes';
import { sampleScreening } from '../utils/sampleRecruitment';

const ID_PREFIX = 'applicant-screening';

/** What the attachment column accepts — the server's `user-documents` category, verified by magic bytes. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const MAX_BYTES = 10 * 1024 * 1024;

/** `''` is unanswered — a required outcome opens EMPTY, never pre-read as either. */
type OutcomeChoice = '' | '0' | '1';

export type ScreeningStepKey = 'checklist' | 'screening';
type StepKey = ScreeningStepKey;

/**
 * One line of the checklist as the form holds it. `requirementId` is null on a line the reader ADDED
 * here — a STAGED requirement, written to Settings only when the form saves.
 */
interface ChecklistLine {
  key: string;
  requirementId: string | null;
  name: string;
  description: string | null;
  /** False on a requirement deleted from Settings — on the list only because it was answered. */
  isActive: boolean;
  /** Complied? Yes needs a file on the line — one filed already, or one chosen here. */
  requiresAttachment: boolean;
  /** Complied? — null until answered. Required on every line. */
  complied: boolean | null;
  /** The file already filed for this line, by name. */
  filedName: string | null;
  /** A file chosen in this form, uploaded after the screening saves. */
  file: File | null;
}

/**
 * Records or corrects one applicant's **Initial Screening**, in TWO STEPS (product call, 19 Oct 2026):
 *
 * 1. **Recruitment checklist** — a table of Requirement · Attachment · Complied? (Yes/No). Every
 *    requirement in Settings is a line; a requirement not defined there yet is ADDED here, on the
 *    table's own `+` row — STAGED, and written to Settings (`applicant-requirements.create`) only
 *    when the form saves, so an abandoned form leaves nothing behind.
 * 2. **Screening** — Screened by, Screened at, Remarks, and the Qualification.
 *
 * **The write order**: staged requirements first (each under its own key), then the screening (which
 * answers every line and SETS the status), then each chosen FILE against its now-saved line. A file
 * that fails after the screening saved is reported, never silently dropped.
 */
export function ApplicantScreeningModal({
  applicant,
  applicantName,
  initialStep = 'checklist',
  onClose,
  onSaved,
}: {
  applicant: ApplicantListItem;
  applicantName: string;
  /** The step the form opens on — the profile card that opened it. Forward jumps stay gated by validity. */
  initialStep?: StepKey;
  onClose: () => void;
  onSaved: (saved: ApplicantListItem) => void;
}) {
  const toast = useToast();
  const { user, hasPermission } = useAuth();
  const accounts = useRecruitmentUsers();
  const canAddRequirement = hasPermission('applicant-requirements.create');

  const [step, setStep] = useState(initialStep === 'screening' ? 1 : 0);
  const [lines, setLines] = useState<ChecklistLine[]>([]);
  const [outcome, setOutcome] = useState<OutcomeChoice>('');
  // A first screening opens on the signed-in account and today, so it opens answerable.
  const [screenedBy, setScreenedBy] = useState<string | null>(user?.id ?? null);
  const [screenedDate, setScreenedDate] = useState(todayCalendarDate());
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    applicantsApi
      .screening(applicant.id, controller.signal)
      .then((loaded) => {
        setLines(
          loaded.checklist.map((c) => ({
            key: c.requirementId,
            requirementId: c.requirementId,
            name: c.name,
            description: c.description ?? null,
            isActive: c.isActive,
            requiresAttachment: c.requiresAttachment === true,
            complied: c.isComplied ?? null,
            filedName: c.attachmentFileName ?? null,
            file: null,
          })),
        );

        // A re-screen opens on what was recorded.
        if (loaded.screeningResult != null) {
          setOutcome(String(loaded.screeningResult) as OutcomeChoice);
          setScreenedBy(loaded.screenedByUserId ?? null);
          setScreenedDate(loaded.screenedDate ?? todayCalendarDate());
          setRemarks(loaded.screeningRemarks ?? '');
        }

        setLoading(false);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoading(false);
        // No focus call: a failed load names no field.
        setError(caught instanceof ApiError ? caught : new ApiError('The checklist could not be loaded.', 0));
      });

    return () => controller.abort();
  }, [applicant.id]);

  const stagedIncomplete = lines.some((l) => l.requirementId === null && l.name.trim() === '');
  /** A line that needs a file and has none — filed already or chosen here. Yes cannot stand on it. */
  const lacksFile = (l: ChecklistLine) => l.requiresAttachment && l.filedName == null && l.file === null;
  const checklistValid =
    !loading && lines.every((l) => l.complied !== null && !(l.complied && lacksFile(l))) && !stagedIncomplete;
  const screeningValid = outcome !== '' && screenedBy != null && screenedBy !== '' && screenedDate.length > 0;

  const steps: { key: StepKey; label: string; valid: boolean }[] = [
    { key: 'checklist', label: 'Recruitment checklist', valid: checklistValid },
    { key: 'screening', label: 'Screening', valid: screeningValid },
  ];

  const isLastStep = step === steps.length - 1;
  const canSave = checklistValid && screeningValid && confirmed && !saving;
  const disqualifying = outcome === String(APPLICANT_QUALIFICATION.Disqualified);

  function update(key: string, patch: Partial<ChecklistLine>) {
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setLines((current) => [
      ...current,
      {
        key: `staged-${uid()}`,
        requirementId: null,
        name: '',
        description: null,
        isActive: true,
        requiresAttachment: false,
        complied: null,
        filedName: null,
        file: null,
      },
    ]);
  }

  function generate() {
    const sample = sampleScreening(accounts.users);

    // Every line answered; a staged line left blank takes a synthetic name. Files are NOT generated:
    // the server verifies them by magic bytes, and a fabricated file would not be one.
    setLines((current) =>
      current.map((l) => ({
        ...l,
        name: l.requirementId === null && l.name.trim() === '' ? sample.requirementName() : l.name,
        // A line that needs a file it does not have answers No — Yes would be refused.
        complied: lacksFile(l) ? false : Math.random() < 0.7,
      })),
    );
    setOutcome(String(sample.result) as OutcomeChoice);
    if (sample.screenedByUserId) setScreenedBy(sample.screenedByUserId);
    setScreenedDate(sample.screenedDate);
    setRemarks(sample.remarks);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      // 1 — STAGED requirements, each under the catalog's own key. A line that landed keeps its new id,
      //     so a retry after a later failure never writes it twice.
      let current = lines;

      for (const line of current.filter((l) => l.requirementId === null)) {
        const created = await applicantRequirementsApi.create({
          name: line.name.trim(),
          description: null,
          requiresAttachment: false,
        });
        current = current.map((l) => (l.key === line.key ? { ...l, requirementId: created.id } : l));
        setLines(current);
      }

      // 2 — the screening: every line answered, the status set to match the outcome.
      const saved = await applicantsApi.screen(applicant.id, {
        screeningResult: Number(outcome) as ApplicantQualificationValue,
        screenedByUserId: screenedBy ?? '',
        screenedDate,
        screeningRemarks: remarks.trim() || null,
        requirements: current.map((l) => ({
          requirementId: l.requirementId ?? '',
          isComplied: l.complied === true,
          attachmentToFollow: l.file !== null,
        })),
      });

      // 3 — each chosen FILE, against its now-saved line. The screening is saved whatever happens here.
      const failed: string[] = [];

      for (const line of current.filter((l) => l.file !== null && l.requirementId !== null)) {
        try {
          await applicantsApi.attachRequirementFile(applicant.id, line.requirementId ?? '', line.file as File);
        } catch (caught) {
          failed.push(`${line.name} (${caught instanceof ApiError ? caught.message : 'upload failed'})`);
        }
      }

      if (failed.length > 0) {
        // The form has nothing left to report in — the screening saved — so the failure is a toast.
        toast.failure(`Screening saved, but ${failed.length === 1 ? 'a file' : 'some files'} could not be attached: ${failed.join('; ')}`);
      } else {
        toast.success(`Screened — ${saved.screeningResultLabel ?? 'recorded'}.`);
      }

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the screening.', 0);
      setError(failure);
      // A refusal about the checklist sends the reader back to it.
      if (failure.fieldError('requirements') || failure.fieldError('name')) setStep(0);
      focusValidationError(failure, {
        idPrefix: ID_PREFIX,
        // A <RadioGroup> puts its id on each OPTION; the checklist is a table, not one input.
        fieldIds: {
          screeningResult: `${ID_PREFIX}-screening-result-0`,
          requirements: `${ID_PREFIX}-checklist`,
          name: `${ID_PREFIX}-checklist`,
        },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Initial screening"
      size="3xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || loading || accounts.loading }}
      confirm={
        isLastStep ? { id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined
      }
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {/* Back never validates — it is how an invalid step gets fixed. */}
          {step > 0 ? (
            <Button id={`${ID_PREFIX}-back`} variant="secondary" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={saving}>
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Button
              id={`${ID_PREFIX}-next`}
              variant="secondary"
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              disabled={!steps[step].valid}
            >
              Next
            </Button>
          ) : null}

          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Stepper
          id={ID_PREFIX}
          steps={steps.map(({ key, label }) => ({ key, label }))}
          activeIndex={step}
          onJump={(index) => {
            // Forward requires every earlier step to be valid; back is free.
            if (index <= step || steps.slice(0, index).every((s) => s.valid)) setStep(index);
          }}
        />

        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {steps[step].key === 'checklist' ? (
          <>
            <p className="text-xs text-slate-500">
              Screening <span className="text-slate-700">{applicantName}</span>. Answer Complied? on every requirement and
              attach what the applicant submitted.
              {canAddRequirement
                ? ' A requirement not in Settings yet is added on the last row, and saved to Settings with the screening.'
                : ''}
            </p>

            {loading ? (
              <div className="flex flex-col gap-2">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              <div id={`${ID_PREFIX}-checklist`} tabIndex={-1} className="focus:outline-none">
                <InlineAddTable
                  headers={['Requirement *', 'Attachment', 'Complied? *']}
                  colWidths={[300, 280, 140]}
                  flexColumn={0}
                  rowCount={lines.length}
                  onAddRow={addLine}
                  addButtonId={`${ID_PREFIX}-add-requirement`}
                  addDisabled={!canAddRequirement || stagedIncomplete || saving}
                  addDisabledReason={
                    !canAddRequirement
                      ? 'Adding a requirement needs the Applicant Requirements create permission. Ask for it, or add it under Recruitment › Settings.'
                      : 'Name the requirement you added before adding another.'
                  }
                >
                  {lines.map((line, index) => (
                    <InlineRow
                      key={line.key}
                      idx={index + 1}
                      onRemove={() => setLines((current) => current.filter((l) => l.key !== line.key))}
                      // A line from Settings stays on every checklist; only a line added HERE comes off.
                      menu={
                        line.requirementId === null
                          ? undefined
                          : {
                              id: `${ID_PREFIX}-line-${line.key}-menu`,
                              items: [
                                {
                                  key: 'remove',
                                  label: 'Remove',
                                  disabledReason:
                                    'A requirement from Settings is on every checklist. Delete it under Recruitment › Settings to take it off.',
                                  onClick: () => undefined,
                                },
                              ],
                            }
                      }
                    >
                      {line.requirementId === null ? (
                        <TdInput
                          id={`${ID_PREFIX}-line-${line.key}-name`}
                          ariaLabel={`Requirement on row ${index + 1}`}
                          placeholder="Name the requirement, e.g. Personal Data Sheet"
                          value={line.name}
                          onChange={(name) => update(line.key, { name })}
                          disabled={saving}
                        />
                      ) : (
                        <TdSeamless
                          below={
                            line.isActive ? (line.description ?? undefined) : 'Deleted from Settings — kept for this answer'
                          }
                        >
                          <span className="truncate text-sm text-slate-600">{line.name}</span>
                        </TdSeamless>
                      )}

                      <AttachmentCell
                        id={`${ID_PREFIX}-line-${line.key}-file`}
                        rowLabel={line.name || `row ${index + 1}`}
                        line={line}
                        disabled={saving}
                        // Taking the file off a line that needs one takes its Yes with it.
                        onChoose={(file) =>
                          update(line.key, {
                            file,
                            ...(file === null && line.requiresAttachment && line.filedName == null && line.complied
                              ? { complied: null }
                              : {}),
                          })
                        }
                        onReject={(message) => toast.failure(message)}
                      />

                      <TdSelect
                        id={`${ID_PREFIX}-line-${line.key}-complied`}
                        ariaLabel={`Complied? for ${line.name || `row ${index + 1}`}`}
                        value={line.complied === null ? null : line.complied ? 'yes' : 'no'}
                        onChange={(value) => update(line.key, { complied: value === '' ? null : value === 'yes' })}
                        options={[
                          // Yes needs the file first on a requirement that asks for one (22 Oct 2026).
                          { value: 'yes', label: 'Yes', disabled: lacksFile(line) },
                          { value: 'no', label: 'No' },
                        ]}
                        // Yes before No — the order the question is asked in, not the alphabet's.
                        sort="none"
                        disabled={saving}
                      />
                    </InlineRow>
                  ))}
                </InlineAddTable>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500">
              Saving sets the application status to <span className="text-slate-700">Qualified</span> or{' '}
              <span className="text-slate-700">Not Qualified</span> to match the outcome.
              {disqualifying && applicant.interviewDate != null ? ' Disqualifying also clears the interview already scheduled.' : ''}
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                id={`${ID_PREFIX}-screened-by-user-id`}
                label="Screened by"
                required
                description={accounts.error ?? 'The account that screened the applicant — not necessarily whoever records it here.'}
                placeholder="Choose an account…"
                value={screenedBy}
                onChange={(value) => setScreenedBy(value || null)}
                options={accounts.users.map((u) => ({ value: u.userId, label: u.displayName }))}
                loading={accounts.loading}
                disabled={saving || loading}
                error={screenedBy ? error?.fieldError('screenedByUserId') : 'Choose who screened the applicant.'}
              />

              <DateField
                id={`${ID_PREFIX}-screened-date`}
                label="Screened at"
                required
                description="The day of the screening — not before the application was received, not after today."
                min={applicant.dateApplied}
                max={todayCalendarDate()}
                value={screenedDate}
                onChange={setScreenedDate}
                error={error?.fieldError('screenedDate')}
                disabled={saving || loading}
              />
            </div>

            {/* A free note about the screening as a whole — full width, outside the grid. */}
            <Textarea
              id={`${ID_PREFIX}-screening-remarks`}
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              error={error?.fieldError('screeningRemarks')}
              disabled={saving || loading}
            />

            {/* LAST, in the product owner's order: the verdict follows the checklist and the notes. */}
            <RadioGroup<OutcomeChoice>
              id={`${ID_PREFIX}-screening-result`}
              label="Qualification"
              required
              description="Qualified moves the applicant on to the initial interview; Disqualified ends the application."
              value={outcome}
              onChange={setOutcome}
              options={APPLICANT_QUALIFICATION_OPTIONS.map((o) => ({ value: String(o.value) as OutcomeChoice, label: o.label }))}
              columns={2}
              error={outcome === '' ? 'Choose Qualified or Disqualified.' : error?.fieldError('screeningResult')}
              disabled={saving || loading}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * The Attachment cell: **Attach** on an empty line; once there is a file — chosen in this form, or
 * already filed — its NAME is the control, and clicking it replaces the file (product call, 19 Oct
 * 2026: no separate Replace). Downloading a filed one is the profile's Recruitment checklist card.
 * The input is the native file picker, visually hidden; nothing is uploaded until the form saves.
 */
function AttachmentCell({
  id,
  rowLabel,
  line,
  disabled,
  onChoose,
  onReject,
}: {
  id: string;
  rowLabel: string;
  line: ChecklistLine;
  disabled: boolean;
  onChoose: (file: File | null) => void;
  onReject: (message: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const fileName = line.file?.name ?? line.filedName;

  return (
    <TdSeamless>
      <div className="flex min-w-0 items-center gap-2">
        <input
          ref={input}
          id={id}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-label={`Attachment for ${rowLabel}`}
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            e.target.value = '';
            if (file && file.size > MAX_BYTES) {
              onReject(`${file.name} is larger than 10 MB.`);
              return;
            }
            onChoose(file);
          }}
        />

        {/*
          LIGHTER than the "clickable is black" default, by product call (19 Oct 2026) — a cell's action
          should not outweigh the values in the grid. The underline on hover and the hand cursor stay.
        */}
        {fileName != null ? (
          <Tooltip label={`Replace ${fileName}`} block>
            <button
              id={`${id}-choose`}
              type="button"
              onClick={() => input.current?.click()}
              disabled={disabled}
              className="inline-flex min-w-0 max-w-full items-center gap-1 text-sm text-slate-600 hover:underline focus:outline-none focus-visible:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 truncate">{fileName}</span>
              <span className="sr-only">, replace</span>
            </button>
          </Tooltip>
        ) : (
          <button
            id={`${id}-choose`}
            type="button"
            onClick={() => input.current?.click()}
            disabled={disabled}
            className="inline-flex shrink-0 items-center gap-1 text-sm text-slate-500 hover:underline focus:outline-none focus-visible:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
          >
            <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
            Attach
          </button>
        )}
      </div>
    </TdSeamless>
  );
}
